// NewKBFilters.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  HStack,
  InputGroup,
  InputLeftElement,
  Icon,
  Input,
  Select,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  MenuGroup,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  Flex,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  ButtonGroup,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  Badge,
  Tooltip
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, PhoneIcon, ChevronDownIcon } from '@chakra-ui/icons';
import NewKBDateFilter from './NewKBDateFilter';
import { useSocket } from '../../../../contexts/SocketContext';

const NewKBFilters = ({
  searchQuery, setSearchQuery,
  callFilter, setCallFilter,
  tagFilter, setTagFilter,
  sourceFilter, setSourceFilter, // Add new source filter props
  assigneeFilter, setAssigneeFilter,
  leadOwnerFilter, setLeadOwnerFilter, // Add lead owner filter props
  missedFollowUpFilter, setMissedFollowUpFilter,
  todayFollowUpFilter, setTodayFollowUpFilter,
  appliedDateFilter, setAppliedDateFilter,
  dateFilter, setDateFilter,
  datePreset, setDatePreset,
  focusedInput, setFocusedInput,
  users,
  handleAddNewLead,
  onCallRangeChange
}) => {
  const { socket } = useSocket();

  const [callRange, setCallRange] = useState(() => {
    // Initialize based on current callFilter
    if (callFilter === 'custom_range') {
      const urlParams = new URLSearchParams(window.location.search);
      const min = parseInt(urlParams.get('custom_range_min') || '0', 10);
      const max = parseInt(urlParams.get('custom_range_max') || '20', 10);
      return [min, max];
    }
    // Default range
    return [0, 20];
  });
  
  const [isCallRangeOpen, setIsCallRangeOpen] = useState(false);
  
  // All available tag options
  const tagOptions = {
    // Tags section
    tags: [
      'Hot', 'Not Responding', 'Fake Lead', 'Inquiring', 'Low Budget',
      'Venues Pending', 'Proposal Pending', 'Discuss', 'Corporate',
      'Calculator', 'Decor Only', 'Chattarpur Farm', 'Weekend',
      'Text Preferred', 'Shivam', 'Other', 'Dates Not Fixed', 'Venues Sent', 'NRI Client'
    ],
    // Sources section (from lead sources in constants)
    sources: [
      'Website', 'Referral', 'Social Media', 'Direct', 'Calculator',
      'IG DM', 'Organic', 'Shivam', 'Other'
    ]
  };
  
  // Handle tag selection/deselection
  const handleTagSelect = (tag) => {
    if (tag === "") {
      // Clear all tags when "All Tags" is selected
      setTagFilter([]);
      return;
    }
    
    if (tag === "no_tags") {
      // Special case: No Tags is exclusive
      setTagFilter(["no_tags"]);
      return;
    }
    
    // Remove 'no_tags' if it exists when selecting any tag
    let newTagFilter = tagFilter.filter(t => t !== "no_tags");
    
    // Toggle the tag (add if not present, remove if present)
    if (newTagFilter.includes(tag)) {
      newTagFilter = newTagFilter.filter(t => t !== tag);
    } else {
      newTagFilter = [...newTagFilter, tag];
    }
    
    setTagFilter(newTagFilter);
  };
  
  // Handle source selection/deselection
  const handleSourceSelect = (source) => {
    if (source === "") {
      // Clear all sources
      setSourceFilter([]);
      return;
    }
    
    // Toggle the source (add if not present, remove if present)
    let newSourceFilter = [...sourceFilter];
    
    if (newSourceFilter.includes(source)) {
      newSourceFilter = newSourceFilter.filter(s => s !== source);
    } else {
      newSourceFilter = [...newSourceFilter, source];
    }
    
    setSourceFilter(newSourceFilter);
  };
  
  // Handle assignee selection
  const handleAssigneeSelect = (userId) => {
    // Set the assignee filter to the selected user ID or clear if empty
    setAssigneeFilter(userId);
  };
  
  // Handle lead owner selection
  const handleLeadOwnerSelect = (userId) => {
    // Set the lead owner filter to the selected user ID or clear if empty
    setLeadOwnerFilter(userId);
  };
  
  // Function to get display name for call filter
  const getCallFilterDisplay = () => {
    switch(callFilter) {
      case 'no_calls': return 'No Calls';
      case 'never_picked_up': return 'Never Picked Up';
      case 'one_call': return '1 Call';
      case 'two_calls': return '2 Calls';
      case 'less_than_3': return '≤ 2 Calls';
      case 'less_than_5': return '≤ 4 Calls';
      case 'more_than_5': return '≥ 5 Calls';
      case 'custom_range': 
        if (callRange[0] === callRange[1]) {
          return `${callRange[0]} Call${callRange[0] !== 1 ? 's' : ''}`;
        }
        return `${callRange[0]} - ${callRange[1]} Calls`;
      default: return 'All Calls';
    }
  };
  
  // Update call range and notify parent component
  const handleCallRangeChange = (newRange) => {
    setCallRange(newRange);
    if (onCallRangeChange) {
      onCallRangeChange(newRange[0], newRange[1]);
    }
  };

  // Handle applying a custom call range
  const handleFilterByCallRange = () => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (callRange[0] === 0 && callRange[1] === 0) {
      setCallFilter('no_calls');
      urlParams.delete('custom_range_min');
      urlParams.delete('custom_range_max');
    } else if (callRange[0] === 0 && callRange[1] === 1) {
      setCallFilter('one_call');
      urlParams.delete('custom_range_min');
      urlParams.delete('custom_range_max');
    } else if (callRange[0] === 0 && callRange[1] === 2) {
      setCallFilter('less_than_3');
      urlParams.delete('custom_range_min');
      urlParams.delete('custom_range_max');
    } else if (callRange[0] === 0 && callRange[1] === 4) {
      setCallFilter('less_than_5');
      urlParams.delete('custom_range_min');
      urlParams.delete('custom_range_max');
    } else if (callRange[0] === 5 && callRange[1] >= 5) {
      setCallFilter('more_than_5');
      urlParams.delete('custom_range_min');
      urlParams.delete('custom_range_max');
    } else {
      // Set custom range parameters in URL
      urlParams.set('custom_range_min', callRange[0].toString());
      urlParams.set('custom_range_max', callRange[1].toString());
      setCallFilter('custom_range');
      
      // Notify parent component about the range change
      if (onCallRangeChange) {
        onCallRangeChange(callRange[0], callRange[1]);
      }
    }
    
    // Update URL with the new parameters without refreshing the page
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${urlParams}`
    );
    
    setIsCallRangeOpen(false);
  };
  
  // Listen for socket events that might affect our filters
  useEffect(() => {
    if (socket) {
      // When a call is added, we might need to update our UI if we're filtering by calls
      const handleCallAdded = (data) => {
        if (callFilter) {
          console.log('[NewKBFilters] Detected call:added event that might affect filters:', {
            leadId: data.leadId,
            callCount: data.callCount,
            currentFilter: callFilter
          });
          // If we're using a call filter, we might want to refresh or update the UI
          // The actual filtering logic is handled in NewKBLeadContext
        }
      };

      // When a lead is updated, we should check if call count changed
      const handleLeadUpdated = (updatedLead) => {
        if (callFilter && updatedLead.callCount !== undefined) {
          console.log('[NewKBFilters] Detected lead update with call count change:', {
            leadId: updatedLead.leadId,
            callCount: updatedLead.callCount,
            currentFilter: callFilter
          });
          // The actual filtering is handled in NewKBLeadContext
        }
      };

      // Socket event handlers registered

      // Register event listeners
      socket.on('call:added', handleCallAdded);
      socket.on('lead:updated', handleLeadUpdated);

      // Cleanup function
      return () => {
        socket.off('call:added', handleCallAdded);
        socket.off('lead:updated', handleLeadUpdated);
      };
    }
  }, [socket, callFilter]);

  return (
    <Box mb={1} px={2} pt={0}>
      <HStack spacing={1} mb={1} justify="space-between">
        <Box flex={1}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none" h="30px">
              <Icon as={SearchIcon} color="gray.400" boxSize={3} />
            </InputLeftElement>
            <Input
              placeholder="Search leads by name, phone, or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
              height="30px"
            />
          </InputGroup>
        </Box>
          
        <ButtonGroup size="sm" spacing={1}>
          {/* Call Filter */}
          <Popover 
            isOpen={isCallRangeOpen} 
            onClose={() => setIsCallRangeOpen(false)}
            placement="bottom-end"
          >
            <PopoverTrigger>
              <Button
                size="sm"
                onClick={() => setIsCallRangeOpen(!isCallRangeOpen)}
                colorScheme={callFilter ? "blue" : "gray"}
                leftIcon={<PhoneIcon />}
              >
                {getCallFilterDisplay()}
              </Button>
            </PopoverTrigger>
            <PopoverContent p={3} width="250px">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader fontWeight="medium">Filter by Call Count</PopoverHeader>
              <PopoverBody>
                <Box mb={3}>
                  <Text mb={2}>Select Range:</Text>
                  <RangeSlider
                    defaultValue={callRange}
                    min={0}
                    max={20}
                    step={1}
                    onChange={handleCallRangeChange}
                    mb={2}
                  >
                    <RangeSliderTrack bg="gray.200">
                      <RangeSliderFilledTrack bg="blue.500" />
                    </RangeSliderTrack>
                    <RangeSliderThumb index={0} boxSize={6}>
                      <Box color="blue.500" as={PhoneIcon} />
                    </RangeSliderThumb>
                    <RangeSliderThumb index={1} boxSize={6}>
                      <Box color="blue.500" as={PhoneIcon} />
                    </RangeSliderThumb>
                  </RangeSlider>
                  <Flex justify="space-between">
                    <NumberInput 
                      size="xs" 
                      maxW="70px" 
                      min={0} 
                      max={callRange[1]} 
                      value={callRange[0]}
                      onChange={(val) => handleCallRangeChange([parseInt(val, 10), callRange[1]])}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text>to</Text>
                    <NumberInput 
                      size="xs" 
                      maxW="70px" 
                      min={callRange[0]} 
                      max={20} 
                      value={callRange[1]}
                      onChange={(val) => handleCallRangeChange([callRange[0], parseInt(val, 10)])}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </Flex>
                </Box>
                <Box mb={3}>
                  <Text mb={2}>Presets:</Text>
                  <HStack spacing={2} wrap="wrap">
                    <Button size="xs" onClick={() => { setCallFilter('all'); setIsCallRangeOpen(false); }}>All</Button>
                    <Button size="xs" onClick={() => { setCallFilter('no_calls'); setIsCallRangeOpen(false); }}>No Calls</Button>
                    <Button size="xs" onClick={() => { setCallFilter('never_picked_up'); setIsCallRangeOpen(false); }}>Never Picked Up</Button>
                    <Button size="xs" onClick={() => { setCallFilter('one_call'); setIsCallRangeOpen(false); }}>1 Call</Button>
                    <Button size="xs" onClick={() => { setCallFilter('less_than_3'); setIsCallRangeOpen(false); }}>≤ 2 Calls</Button>
                    <Button size="xs" onClick={() => { setCallFilter('less_than_5'); setIsCallRangeOpen(false); }}>≤ 4 Calls</Button>
                    <Button size="xs" onClick={() => { setCallFilter('more_than_5'); setIsCallRangeOpen(false); }}>≥ 5 Calls</Button>
                  </HStack>
                </Box>
                <Button size="sm" colorScheme="blue" onClick={handleFilterByCallRange} isFullWidth>
                  Apply Custom Range
                </Button>
              </PopoverBody>
            </PopoverContent>
          </Popover>

          {/* Date Filter */}
          <NewKBDateFilter
            appliedDateFilter={appliedDateFilter}
            setAppliedDateFilter={setAppliedDateFilter}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            datePreset={datePreset}
            setDatePreset={setDatePreset}
            focusedInput={focusedInput}
            setFocusedInput={setFocusedInput}
          />

          {/* Follow-up Filters - Missed and Today's */}
          <HStack spacing={1}>
            <Button
              size="sm"
              variant="outline"
              colorScheme={missedFollowUpFilter ? "red" : "gray"}
              onClick={() => {
                setMissedFollowUpFilter(!missedFollowUpFilter);
                if (!missedFollowUpFilter && todayFollowUpFilter) {
                  setTodayFollowUpFilter(false); // Turn off other filter when this one is activated
                }
              }}
            >
              Missed Follow-ups
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme={todayFollowUpFilter ? "green" : "gray"}
              onClick={() => {
                setTodayFollowUpFilter(!todayFollowUpFilter);
                if (!todayFollowUpFilter && missedFollowUpFilter) {
                  setMissedFollowUpFilter(false); // Turn off other filter when this one is activated
                }
              }}
            >
              Today's Follow-ups
            </Button>
          </HStack>

          {/* Tag Filter - more compact */}
          <Menu closeOnSelect={false}>
            <MenuButton 
              as={Button} 
              size="sm" 
              variant="outline" 
              rightIcon={<ChevronDownIcon boxSize={3} />} 
              colorScheme={tagFilter.length > 0 ? "purple" : "gray"}
            >
              {tagFilter.length === 0 ? "Tags" : 
               tagFilter.length === 1 ? tagFilter[0] : 
               `Tags (${tagFilter.length})`}
            </MenuButton>
            <MenuList maxH="300px" overflowY="auto">
              <MenuItem onClick={() => handleTagSelect("")}><em>All Tags</em></MenuItem>
              <MenuItem onClick={() => handleTagSelect("no_tags")}>
                {tagFilter.includes("no_tags") && "✓ "}No Tags
              </MenuItem>
              <MenuDivider />
              
              {/* Tags Section */}
              <MenuGroup title="Tags">
                {tagOptions.tags.map(tag => (
                  <MenuItem key={tag} onClick={() => handleTagSelect(tag)}>
                    {tagFilter.includes(tag) && "✓ "}{tag}
                  </MenuItem>
                ))}
              </MenuGroup>
            </MenuList>
          </Menu>

          {/* Lead Source Filter - new dedicated dropdown */}
          <Menu closeOnSelect={false}>
            <MenuButton 
              as={Button} 
              size="sm" 
              variant="outline" 
              rightIcon={<ChevronDownIcon boxSize={3} />} 
              colorScheme={sourceFilter.length > 0 ? "orange" : "gray"}
            >
              {sourceFilter.length === 0 ? "Sources" : 
               sourceFilter.length === 1 ? sourceFilter[0] : 
               `Sources (${sourceFilter.length})`}
            </MenuButton>
            <MenuList maxH="300px" overflowY="auto">
              <MenuItem onClick={() => handleSourceSelect("")}><em>All Sources</em></MenuItem>
              <MenuDivider />
              
              {/* Sources Section */}
              <MenuGroup title="Lead Sources">
                {tagOptions.sources.map(source => (
                  <MenuItem key={`source_${source}`} onClick={() => handleSourceSelect(source)}>
                    {sourceFilter.includes(source) && "✓ "}{source}
                  </MenuItem>
                ))}
              </MenuGroup>
            </MenuList>
          </Menu>

          {/* Assignee Filter */}
          <Menu closeOnSelect={true}>
            <MenuButton 
              as={Button} 
              size="sm" 
              variant="outline" 
              rightIcon={<ChevronDownIcon boxSize={3} />} 
              colorScheme={assigneeFilter ? "teal" : "gray"}
            >
              {!assigneeFilter ? "Assignee" : 
               users.find(u => u.uid === assigneeFilter)?.displayName || "Assigned"}
            </MenuButton>
            <MenuList maxH="300px" overflowY="auto">
              <MenuItem onClick={() => handleAssigneeSelect("")}><em>All Users</em></MenuItem>
              <MenuDivider />
              
              {/* Users Section */}
              <MenuGroup title="Assigned To">
                {users && users.map(user => (
                  <MenuItem key={user.uid} onClick={() => handleAssigneeSelect(user.uid)}>
                    {assigneeFilter === user.uid && "✓ "}{user.displayName || user.email}
                  </MenuItem>
                ))}
                {(!users || users.length === 0) && (
                  <MenuItem isDisabled>No users available</MenuItem>
                )}
              </MenuGroup>
            </MenuList>
          </Menu>
          
          {/* Lead Owner Filter */}
          <Menu closeOnSelect={true}>
            <MenuButton 
              as={Button} 
              size="sm" 
              variant="outline" 
              rightIcon={<ChevronDownIcon boxSize={3} />} 
              colorScheme={leadOwnerFilter ? "cyan" : "gray"}
            >
              {!leadOwnerFilter ? "Owner" : 
               users.find(u => u.uid === leadOwnerFilter)?.displayName || "Owned"}
            </MenuButton>
            <MenuList maxH="300px" overflowY="auto">
              <MenuItem onClick={() => handleLeadOwnerSelect("")}><em>All Owners</em></MenuItem>
              <MenuDivider />
              
              {/* Users Section */}
              <MenuGroup title="Lead Owner">
                {users && users.map(user => (
                  <MenuItem key={`owner-${user.uid}`} onClick={() => handleLeadOwnerSelect(user.uid)}>
                    {leadOwnerFilter === user.uid && "✓ "}{user.displayName || user.email}
                  </MenuItem>
                ))}
                {(!users || users.length === 0) && (
                  <MenuItem isDisabled>No users available</MenuItem>
                )}
              </MenuGroup>
            </MenuList>
          </Menu>

          {/* Add New Lead Button - More compact */}
          <Tooltip label="Add New Lead" hasArrow>
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={<AddIcon boxSize={3} />}
              onClick={handleAddNewLead}
            >
              Add
            </Button>
          </Tooltip>
        </ButtonGroup>
      </HStack>

      {/* Active Filters - More compact inline display */}
      {(searchQuery || callFilter !== 'all' || tagFilter.length > 0 || sourceFilter.length > 0 || assigneeFilter || leadOwnerFilter || missedFollowUpFilter || todayFollowUpFilter || appliedDateFilter.startDate) && (
        <Box mb={1}>
          <Flex wrap="wrap" gap={1} alignItems="center">
            <Text fontSize="2xs" color="gray.600" fontWeight="medium">Filters:</Text>
            {searchQuery && (
              <Badge colorScheme="blue" variant="subtle" fontSize="2xs" py={0}>
                {searchQuery}
              </Badge>
            )}
            {callFilter && callFilter !== 'all' && (
              <Badge colorScheme="blue" variant="subtle" fontSize="2xs" py={0}>
                {getCallFilterDisplay()}
              </Badge>
            )}
            {tagFilter.length > 0 && (
              <Flex gap={1}>
                {tagFilter.map(tag => (
                  <Badge key={tag} colorScheme="purple" variant="subtle" fontSize="2xs" py={0}>
                    {tag}
                  </Badge>
                ))}
              </Flex>
            )}
            {sourceFilter.length > 0 && (
              <Flex gap={1}>
                {sourceFilter.map(source => (
                  <Badge key={source} colorScheme="orange" variant="subtle" fontSize="2xs" py={0}>
                    {source}
                  </Badge>
                ))}
              </Flex>
            )}
            {assigneeFilter && (
              <Badge colorScheme="teal" variant="subtle" fontSize="2xs" py={0}>
                Assigned: {users.find(u => u.uid === assigneeFilter)?.displayName || 'User'}
              </Badge>
            )}
            {leadOwnerFilter && (
              <Badge colorScheme="cyan" variant="subtle" fontSize="2xs" py={0}>
                Owner: {users.find(u => u.uid === leadOwnerFilter)?.displayName || 'User'}
              </Badge>
            )}
            {missedFollowUpFilter && (
              <Badge colorScheme="red" variant="subtle" fontSize="2xs" py={0}>
                Missed Follow-ups
              </Badge>
            )}
            {todayFollowUpFilter && (
              <Badge colorScheme="green" variant="subtle" fontSize="2xs" py={0}>
                Today's Follow-ups
              </Badge>
            )}
            {appliedDateFilter.startDate && appliedDateFilter.endDate && (
              <Badge colorScheme="orange" variant="subtle" fontSize="2xs" py={0}>
                {appliedDateFilter.startDate.format('M/D')} - {appliedDateFilter.endDate.format('M/D')}
              </Badge>
            )}
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default NewKBFilters;
